import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Fuel, 
  CreditCard, 
  TrendingUp, 
  Users, 
  Building2, 
  DollarSign,
  Target,
  Handshake,
  Shield,
  Globe,
  ChevronRight,
  CheckCircle2,
  BarChart3,
  Zap,
  Store,
  Smartphone,
  MapPin,
  Award,
  Layers,
  ArrowUpRight,
  Sparkles,
  PieChart,
  TrendingDown,
  Brain,
  LayoutDashboard,
  Settings,
  Percent,
  BadgeDollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';

const FeaturedPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('whyjoin');

  // Verify admin access
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground">This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'whyjoin', label: 'Why Join FlowFeed', icon: Sparkles },
    { id: 'partnerships', label: 'Hardware Partnerships', icon: Handshake },
    { id: 'payment', label: 'Payment Strategy', icon: CreditCard },
    { id: 'market', label: 'Market Analysis', icon: BarChart3 },
    { id: 'projections', label: '3-Year Projections', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-lg">FlowFeed</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">INVESTOR DECK</span>
            </div>
          </div>
          <div className="text-sm text-slate-400">
            Confidential | {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 border-b border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
                <Globe className="w-4 h-4" />
                US Retail Market Opportunity
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                The Future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Retail POS</span> in America
              </h1>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                FlowFeed is positioned to capture significant market share in the $15B+ US retail POS market through strategic hardware partnerships and an innovative payment processing model.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-slate-800/50 rounded-xl px-5 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-emerald-400">$15.2B</div>
                  <div className="text-sm text-slate-500">US POS Market 2026</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl px-5 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-cyan-400">150K+</div>
                  <div className="text-sm text-slate-500">Gas Stations in USA</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl px-5 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-amber-400">9.7%</div>
                  <div className="text-sm text-slate-500">CAGR Growth Rate</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-400" />
                  Target Verticals
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: Fuel, label: 'Gas Stations & C-Stores', count: '150,000+', growth: '+4.2%' },
                    { icon: Store, label: 'Smoke Shops & Vape Stores', count: '35,000+', growth: '+12%' },
                    { icon: Smartphone, label: 'Mobile & Electronics', count: '25,000+', growth: '+8%' },
                    { icon: Building2, label: 'General Retail Stores', count: '500,000+', growth: '+3%' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <div className="font-medium">{item.label}</div>
                          <div className="text-sm text-slate-500">{item.count} locations</div>
                        </div>
                      </div>
                      <div className="text-emerald-400 text-sm font-medium">{item.growth}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <nav className="sticky top-[73px] z-40 bg-slate-900/80 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content Sections */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Why Join FlowFeed Tab */}
        {activeTab === 'whyjoin' && (
          <div className="space-y-12">
            {/* Hero Value Proposition */}
            <section className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 text-emerald-400 text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                Why Business Owners Choose FlowFeed
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                One Platform. <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">All Your Businesses.</span> Zero Hassle.
              </h2>
              <p className="text-lg text-slate-400 max-w-3xl mx-auto">
                Whether you own 1 store or 100+ locations, FlowFeed gives you complete control with AI-powered insights, 
                the lowest processing fees, and a unified dashboard that works for ANY business type.
              </p>
            </section>

            {/* Key Benefits Grid */}
            <section>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Low Charges */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6 hover:border-emerald-500/40 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                    <Percent className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Lowest Processing Fees</h3>
                  <p className="text-slate-400 mb-4">
                    Save thousands annually with our competitive payment processing rates. No hidden fees, no surprises.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      2.4% + $0.10 (vs 2.9% + $0.30 industry avg)
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      No monthly minimums
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Free hardware with commitment
                    </li>
                  </ul>
                </div>

                {/* AI-Powered */}
                <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-2xl p-6 hover:border-purple-500/40 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                    <Brain className="w-7 h-7 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">AI-Powered Intelligence</h3>
                  <p className="text-slate-400 mb-4">
                    Let AI do the heavy lifting. Automatic insights that tell you what's working and what's not.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      Product margin analysis
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      Location performance scoring
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-purple-400" />
                      Predictive inventory alerts
                    </li>
                  </ul>
                </div>

                {/* Unified Platform */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/40 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-4">
                    <Layers className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">All Business Types</h3>
                  <p className="text-slate-400 mb-4">
                    Gas station, smoke shop, restaurant, retail - manage them ALL from one single platform.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                      11 industry-specific modules
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                      Custom workflows per business
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                      Unified reporting & analytics
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Multi-Location Dashboard Feature */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-2xl p-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm mb-4">
                    <LayoutDashboard className="w-4 h-4" />
                    Multi-Location Control Center
                  </div>
                  <h3 className="text-2xl font-bold mb-4">
                    10 to 100+ Locations? <span className="text-amber-400">One Dashboard.</span>
                  </h3>
                  <p className="text-slate-400 mb-6">
                    Stop juggling multiple systems. FlowFeed's enterprise dashboard gives you a bird's-eye view 
                    of ALL your locations - whether you own 10 gas stations, 50 smoke shops, or 100+ mixed retail stores.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <PieChart className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Consolidated P&L Reports</div>
                        <div className="text-sm text-slate-400">Real-time profit & loss across all locations in one view</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <TrendingDown className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Loss Detection Alerts</div>
                        <div className="text-sm text-slate-400">AI flags which locations are losing money and WHY</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Top Performer Insights</div>
                        <div className="text-sm text-slate-400">See your most profitable locations and replicate success</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Dashboard Preview */}
                <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-300">Location Performance</h4>
                    <span className="text-xs text-slate-500">Live Data</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { name: 'Gas Station #12 - Houston', profit: '+$24,500', status: 'profit', margin: '18%' },
                      { name: 'Smoke Shop - Dallas', profit: '+$8,200', status: 'profit', margin: '32%' },
                      { name: 'C-Store #5 - Austin', profit: '-$2,100', status: 'loss', margin: '-4%' },
                      { name: 'Gas Station #8 - San Antonio', profit: '+$15,800', status: 'profit', margin: '14%' },
                      { name: 'Mobile Shop - Fort Worth', profit: '+$5,400', status: 'profit', margin: '22%' },
                    ].map((loc, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${loc.status === 'profit' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                          <span className="text-sm">{loc.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-sm font-medium ${loc.status === 'profit' ? 'text-emerald-400' : 'text-red-400'}`}>
                            {loc.profit}
                          </span>
                          <span className="text-xs text-slate-500 w-12 text-right">{loc.margin}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Total Monthly P&L</span>
                    <span className="text-xl font-bold text-emerald-400">+$51,800</span>
                  </div>
                </div>
              </div>
            </section>

            {/* AI Analysis Features */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">AI-Powered Business Intelligence</h2>
                  <p className="text-slate-400">Automatic insights that drive profitability</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Product Analysis */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <BadgeDollarSign className="w-5 h-5 text-emerald-400" />
                    Product Margin Analysis
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    AI automatically identifies which products are making you money and which are eating into your profits.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <span className="text-sm">Red Bull Energy (12oz)</span>
                      <span className="text-sm font-medium text-emerald-400">+45% margin</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <span className="text-sm">Marlboro Red Pack</span>
                      <span className="text-sm font-medium text-emerald-400">+28% margin</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                      <span className="text-sm">Generic Phone Chargers</span>
                      <span className="text-sm font-medium text-red-400">-5% margin</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <span className="text-sm">Fountain Drinks</span>
                      <span className="text-sm font-medium text-amber-400">+12% margin</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Brain className="w-4 h-4 text-purple-400 mt-0.5" />
                      <p className="text-sm text-purple-300">
                        <strong>AI Insight:</strong> Consider discontinuing Generic Phone Chargers. 
                        Switch to branded cables - similar stores see 35% better margins.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location Analysis */}
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    Location Performance AI
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Instantly see which locations are thriving and which need attention - with actionable recommendations.
                  </p>
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Gas Station #12</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Top Performer</span>
                      </div>
                      <div className="text-sm text-slate-400">Revenue: $145K/mo | Fuel Margin: 18%</div>
                    </div>
                    <div className="p-3 bg-slate-900/50 rounded-lg border border-red-500/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">C-Store #5</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">Needs Attention</span>
                      </div>
                      <div className="text-sm text-slate-400">Revenue: $28K/mo | Labor Cost: 42%</div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Brain className="w-4 h-4 text-purple-400 mt-0.5" />
                      <p className="text-sm text-purple-300">
                        <strong>AI Insight:</strong> C-Store #5 labor costs are 15% above average. 
                        Consider adjusting shift schedules based on peak hours data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Comparison Table */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6 text-center">Why FlowFeed Wins</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3 font-medium">Feature</th>
                      <th className="text-center p-3 font-medium text-emerald-400">FlowFeed</th>
                      <th className="text-center p-3 font-medium">Square</th>
                      <th className="text-center p-3 font-medium">Traditional POS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {[
                      { feature: 'Multi-Location Dashboard', ff: true, sq: 'partial', tr: false },
                      { feature: 'AI Profit Analysis', ff: true, sq: false, tr: false },
                      { feature: 'All Business Types', ff: true, sq: false, tr: false },
                      { feature: 'Processing Fee < 2.5%', ff: true, sq: false, tr: 'partial' },
                      { feature: 'Gas Station Integration', ff: true, sq: false, tr: 'partial' },
                      { feature: 'White-Label Option', ff: true, sq: false, tr: false },
                      { feature: '21+ Age Verification', ff: true, sq: false, tr: 'partial' },
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="p-3 font-medium">{row.feature}</td>
                        <td className="p-3 text-center">
                          {row.ff === true ? <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-400" /> : '—'}
                        </td>
                        <td className="p-3 text-center">
                          {row.sq === true ? <CheckCircle2 className="w-5 h-5 mx-auto text-slate-400" /> : row.sq === 'partial' ? <span className="text-amber-400">~</span> : <span className="text-slate-600">—</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.tr === true ? <CheckCircle2 className="w-5 h-5 mx-auto text-slate-400" /> : row.tr === 'partial' ? <span className="text-amber-400">~</span> : <span className="text-slate-600">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-purple-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Business?</h3>
              <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
                Join hundreds of business owners who are saving money, making smarter decisions with AI, 
                and managing all their locations from one powerful platform.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-slate-800/50 rounded-xl px-6 py-4 border border-slate-700">
                  <div className="text-3xl font-bold text-emerald-400">$500K</div>
                  <div className="text-sm text-slate-400">Current ARR</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl px-6 py-4 border border-slate-700">
                  <div className="text-3xl font-bold text-cyan-400">200+</div>
                  <div className="text-sm text-slate-400">Active Locations</div>
                </div>
                <div className="bg-slate-800/50 rounded-xl px-6 py-4 border border-slate-700">
                  <div className="text-3xl font-bold text-purple-400">11</div>
                  <div className="text-sm text-slate-400">Industry Verticals</div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Partnerships Tab */}
        {activeTab === 'partnerships' && (
          <div className="space-y-12">
            {/* Gilbarco Partnership */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <Fuel className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Gilbarco Veeder-Root Partnership</h2>
                  <p className="text-slate-400">Passport POS Integration Strategy</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    About Gilbarco
                  </h3>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Market leader with 60%+ US gas station market share</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Passport POS is the industry standard for fuel retail</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>90,000+ stations running Passport systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Open API ecosystem for certified partners</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-emerald-400" />
                    Partnership Plan
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">1</div>
                      <div>
                        <div className="font-medium">Apply for ISV Partner Program</div>
                        <div className="text-sm text-slate-400">Join Gilbarco's certified software vendor network</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">2</div>
                      <div>
                        <div className="font-medium">Get API Access & Documentation</div>
                        <div className="text-sm text-slate-400">Access Passport POS APIs for pump control, pricing, inventory</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">3</div>
                      <div>
                        <div className="font-medium">Complete Certification Process</div>
                        <div className="text-sm text-slate-400">Pass technical review and security audit</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">4</div>
                      <div>
                        <div className="font-medium">Launch Co-Marketing Initiative</div>
                        <div className="text-sm text-slate-400">Joint promotion to 90K+ Passport stations</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6">
                <h4 className="font-semibold mb-3 text-blue-400">Integration Benefits for FlowFeed</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="text-3xl font-bold text-white">90K+</div>
                    <div className="text-sm text-slate-400">Addressable Stations</div>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl font-bold text-white">Real-time</div>
                    <div className="text-sm text-slate-400">Pump Data Access</div>
                  </div>
                  <div className="text-center p-4">
                    <div className="text-3xl font-bold text-white">Official</div>
                    <div className="text-sm text-slate-400">Partner Badge</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Verifone Partnership */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Verifone Partnership</h2>
                  <p className="text-slate-400">Commander POS & Payment Terminal Integration</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-400" />
                    About Verifone
                  </h3>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>35%+ US fuel retail market share</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Commander system powers 50,000+ stations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Global payment terminal manufacturer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      <span>Strong presence in payment processing infrastructure</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-purple-400" />
                    Integration Approach
                  </h3>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-purple-400 mt-1 shrink-0" />
                      <span><strong>Commander API:</strong> Real-time pump control & fuel data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-purple-400 mt-1 shrink-0" />
                      <span><strong>Payment Integration:</strong> Leverage Verifone terminals</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-purple-400 mt-1 shrink-0" />
                      <span><strong>Dual Strategy:</strong> Support both Gilbarco & Verifone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-purple-400 mt-1 shrink-0" />
                      <span><strong>Market Coverage:</strong> Combined 95% of US gas stations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Timeline */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6">Partnership Roadmap</h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
                <div className="space-y-8">
                  {[
                    { quarter: 'Q1 2026', title: 'Apply for Partner Programs', desc: 'Submit applications to Gilbarco ISV and Verifone partner programs', status: 'current' },
                    { quarter: 'Q2 2026', title: 'API Access & Development', desc: 'Begin integration development with sandbox environments', status: 'upcoming' },
                    { quarter: 'Q3 2026', title: 'Certification & Testing', desc: 'Complete technical certification with both platforms', status: 'upcoming' },
                    { quarter: 'Q4 2026', title: 'Production Launch', desc: 'Roll out to first 100 pilot stations', status: 'upcoming' },
                  ].map((item, i) => (
                    <div key={i} className="relative pl-12">
                      <div className={`absolute left-2 w-5 h-5 rounded-full border-2 ${
                        item.status === 'current' 
                          ? 'bg-emerald-500 border-emerald-400' 
                          : 'bg-slate-800 border-slate-600'
                      }`} />
                      <div className="text-sm text-emerald-400 font-medium mb-1">{item.quarter}</div>
                      <div className="font-semibold text-lg">{item.title}</div>
                      <div className="text-slate-400">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Payment Strategy Tab */}
        {activeTab === 'payment' && (
          <div className="space-y-12">
            {/* Payment Models Overview */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Payment Processing Strategy</h2>
                  <p className="text-slate-400">Multiple revenue models for maximum flexibility</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Stripe Connect */}
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center mb-4">
                    <CreditCard className="w-5 h-5 text-indigo-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Stripe Connect</h3>
                  <p className="text-slate-400 text-sm mb-4">Fast to market, reliable infrastructure</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      2.9% + $0.30 per transaction
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Earn 0.25-0.5% platform fee
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Quick onboarding for merchants
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      PCI compliance handled
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="text-xs text-slate-500">Best for:</div>
                    <div className="text-sm font-medium text-indigo-400">Initial launch & small merchants</div>
                  </div>
                </div>

                {/* Finix White-Label */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-6 relative overflow-hidden">
                  <div className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-medium">RECOMMENDED</div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-4">
                    <Shield className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Finix (White-Label PayFac)</h3>
                  <p className="text-slate-400 text-sm mb-4">Own the payment experience</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Full white-label solution
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Set your own pricing (Interchange++)
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Higher margins (0.5-1%+)
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      FlowFeed-branded statements
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="text-xs text-slate-500">Best for:</div>
                    <div className="text-sm font-medium text-emerald-400">Scale & enterprise clients</div>
                  </div>
                </div>

                {/* ISO Model */}
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-4">
                    <Building2 className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">ISO Partnership</h3>
                  <p className="text-slate-400 text-sm mb-4">Traditional merchant services model</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Partner with existing ISOs
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Referral revenue share
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Local processor relationships
                    </li>
                    <li className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Regional expertise
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="text-xs text-slate-500">Best for:</div>
                    <div className="text-sm font-medium text-amber-400">High-volume regional deals</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Finix Deep Dive */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                Finix: Our Path to Becoming a PayFac
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold mb-4 text-slate-300">What is Finix?</h4>
                  <p className="text-slate-400 mb-4">
                    Finix is a payments infrastructure platform that enables software companies to embed payment processing directly into their products. Instead of redirecting to Stripe or Square, FlowFeed can own the entire payment experience.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <Award className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">White-Label Branding</div>
                        <div className="text-sm text-slate-400">Merchants see "FlowFeed Payments" not a third party</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <DollarSign className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Interchange++ Pricing</div>
                        <div className="text-sm text-slate-400">Pay true interchange + small margin, keep the rest</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                      <Users className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Merchant Onboarding API</div>
                        <div className="text-sm text-slate-400">Onboard merchants programmatically within FlowFeed</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-4 text-slate-300">Revenue Comparison</h4>
                  <div className="overflow-hidden rounded-lg border border-slate-700">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="text-left p-3 font-medium">Model</th>
                          <th className="text-right p-3 font-medium">Your Take</th>
                          <th className="text-right p-3 font-medium">Per $1M TPV</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        <tr>
                          <td className="p-3">Stripe Connect</td>
                          <td className="p-3 text-right text-slate-400">0.25-0.5%</td>
                          <td className="p-3 text-right">$2,500-$5,000</td>
                        </tr>
                        <tr className="bg-emerald-500/5">
                          <td className="p-3 font-medium text-emerald-400">Finix White-Label</td>
                          <td className="p-3 text-right text-emerald-400">0.5-1.5%</td>
                          <td className="p-3 text-right text-emerald-400">$5,000-$15,000</td>
                        </tr>
                        <tr>
                          <td className="p-3">ISO Partnership</td>
                          <td className="p-3 text-right text-slate-400">0.1-0.3%</td>
                          <td className="p-3 text-right">$1,000-$3,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-sm text-slate-400">
                    * TPV = Total Payment Volume. Finix provides 3-6x higher revenue per transaction compared to standard affiliate models.
                  </p>
                </div>
              </div>
            </section>

            {/* Indoor vs Outdoor */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6">Gas Station Payment Strategy</h3>
              <p className="text-slate-400 mb-6">
                Gas stations have two distinct payment zones. FlowFeed's strategy is to capture the high-margin indoor segment first.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Store className="w-8 h-8 text-emerald-400" />
                    <div>
                      <h4 className="font-bold text-lg">Indoor Payments (C-Store)</h4>
                      <p className="text-sm text-emerald-400">FlowFeed Controlled</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      All merchandise & food purchases
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Prepay fuel transactions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Higher margins (30-50% on goods)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Use FlowFeed payment processing
                    </li>
                  </ul>
                </div>
                
                <div className="bg-slate-700/30 border border-slate-600 rounded-xl p-6 opacity-75">
                  <div className="flex items-center gap-3 mb-4">
                    <Fuel className="w-8 h-8 text-slate-400" />
                    <div>
                      <h4 className="font-bold text-lg">Outdoor Payments (At-Pump)</h4>
                      <p className="text-sm text-slate-400">Existing Infrastructure</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-slate-400">
                    <li className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Pay-at-pump credit card
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Uses Gilbarco/Verifone terminals
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Lower margins (1-2% on fuel)
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Typically existing processor contracts
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Market Analysis Tab */}
        {activeTab === 'market' && (
          <div className="space-y-12">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">US Retail POS Market</h2>
                  <p className="text-slate-400">Market size, growth, and opportunity analysis</p>
                </div>
              </div>

              {/* Market Stats */}
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                {[
                  { value: '$15.2B', label: 'Market Size 2025', color: 'emerald' },
                  { value: '$28.6B', label: 'Projected 2030', color: 'cyan' },
                  { value: '9.7%', label: 'CAGR', color: 'amber' },
                  { value: '1.2M+', label: 'Retail Locations', color: 'purple' },
                ].map((stat, i) => (
                  <div key={i} className={`bg-${stat.color}-500/10 border border-${stat.color}-500/20 rounded-xl p-5 text-center`}>
                    <div className={`text-3xl font-bold text-${stat.color}-400`}>{stat.value}</div>
                    <div className="text-sm text-slate-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Segment Breakdown */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">Target Segments</h3>
                  <div className="space-y-4">
                    {[
                      { name: 'Gas Stations & C-Stores', count: '150,000+', tam: '$2.4B', share: '16%' },
                      { name: 'Smoke Shops & Vape', count: '35,000+', tam: '$560M', share: '4%' },
                      { name: 'Mobile & Electronics', count: '25,000+', tam: '$400M', share: '3%' },
                      { name: 'Grocery & Convenience', count: '155,000+', tam: '$2.5B', share: '16%' },
                      { name: 'Restaurants & Cafes', count: '660,000+', tam: '$4.0B', share: '26%' },
                    ].map((seg, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{seg.name}</div>
                          <div className="text-sm text-slate-500">{seg.count} locations</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-emerald-400">{seg.tam}</div>
                          <div className="text-xs text-slate-500">TAM</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">FlowFeed Competitive Edge</h3>
                  <div className="space-y-4">
                    {[
                      { title: 'AI-Powered Analytics', desc: 'Real-time insights competitors lack', icon: Zap },
                      { title: 'Industry-Specific', desc: 'Not one-size-fits-all like Square', icon: Target },
                      { title: 'Modern Tech Stack', desc: 'Cloud-native, mobile-first design', icon: Globe },
                      { title: 'Lower TCO', desc: '40% less than legacy systems', icon: DollarSign },
                      { title: 'Integrated Payments', desc: 'Own the full transaction flow', icon: CreditCard },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                          <item.icon className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <div className="font-medium">{item.title}</div>
                          <div className="text-sm text-slate-400">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Competitor Analysis */}
            <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6">Competitive Landscape</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3 font-medium">Feature</th>
                      <th className="text-center p-3 font-medium text-emerald-400">FlowFeed</th>
                      <th className="text-center p-3 font-medium">Square</th>
                      <th className="text-center p-3 font-medium">Toast</th>
                      <th className="text-center p-3 font-medium">Clover</th>
                      <th className="text-center p-3 font-medium">Legacy POS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {[
                      { feature: 'Industry-Specific Modules', ff: true, sq: false, to: 'partial', cl: false, lg: false },
                      { feature: 'AI Analytics', ff: true, sq: false, to: false, cl: false, lg: false },
                      { feature: 'Fuel Pump Integration', ff: true, sq: false, to: false, cl: false, lg: 'partial' },
                      { feature: 'White-Label Option', ff: true, sq: false, to: false, cl: false, lg: false },
                      { feature: 'Age Verification (21+)', ff: true, sq: false, to: false, cl: 'partial', lg: 'partial' },
                      { feature: 'Affordable Pricing', ff: true, sq: true, to: false, cl: 'partial', lg: false },
                      { feature: 'Modern Cloud-Native', ff: true, sq: true, to: true, cl: 'partial', lg: false },
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="p-3 font-medium">{row.feature}</td>
                        {['ff', 'sq', 'to', 'cl', 'lg'].map((col) => (
                          <td key={col} className="p-3 text-center">
                            {row[col] === true ? (
                              <CheckCircle2 className={`w-5 h-5 mx-auto ${col === 'ff' ? 'text-emerald-400' : 'text-slate-400'}`} />
                            ) : row[col] === 'partial' ? (
                              <span className="text-amber-400">~</span>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Regional Focus */}
            <section>
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                Regional Market Focus
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { region: 'Texas', stores: '42,000+', highlight: 'Largest gas station market', growth: '+5.2%' },
                  { region: 'California', stores: '38,000+', highlight: 'High-value transactions', growth: '+3.8%' },
                  { region: 'Florida', stores: '28,000+', highlight: 'Fast-growing retail', growth: '+6.1%' },
                  { region: 'New York', stores: '25,000+', highlight: 'Dense urban retail', growth: '+2.9%' },
                  { region: 'Georgia', stores: '18,000+', highlight: 'Southeast hub', growth: '+4.5%' },
                  { region: 'Other States', stores: '800K+', highlight: 'Nationwide opportunity', growth: '+4.1%' },
                ].map((region, i) => (
                  <div key={i} className="bg-slate-800/30 border border-slate-700 rounded-xl p-5 hover:border-cyan-500/30 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold">{region.region}</h4>
                      <span className="text-emerald-400 text-sm">{region.growth}</span>
                    </div>
                    <div className="text-2xl font-bold text-cyan-400 mb-1">{region.stores}</div>
                    <div className="text-sm text-slate-400">{region.highlight}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* Projections Tab */}
        {activeTab === 'projections' && (
          <div className="space-y-12">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">3-Year Financial Projections</h2>
                  <p className="text-slate-400">Revenue growth and milestone targets</p>
                </div>
              </div>

              {/* Current State */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-lg mb-4">Current Status (2026)</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-400">Live</div>
                    <div className="text-sm text-slate-400">Product Stage</div>
                  </div>
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-400">11</div>
                    <div className="text-sm text-slate-400">Industry Verticals</div>
                  </div>
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <div className="text-3xl font-bold text-cyan-400">50</div>
                    <div className="text-sm text-slate-400">US States Supported</div>
                  </div>
                  <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                    <div className="text-3xl font-bold text-amber-400">$500K</div>
                    <div className="text-sm text-slate-400">Current ARR</div>
                  </div>
                </div>
              </div>

              {/* Year by Year */}
              <div className="space-y-6">
                {/* Year 1 */}
                <div className="bg-gradient-to-r from-emerald-500/5 to-transparent border border-emerald-500/20 rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-emerald-400">Y1</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Year 1: Scale (2026-2027)</h3>
                      <p className="text-slate-400">Aggressive customer acquisition & partnerships</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-bold text-emerald-400">$1.5M</div>
                      <div className="text-sm text-slate-400">Target ARR</div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">300</div>
                      <div className="text-sm text-slate-400">Paying Customers</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$125K/mo</div>
                      <div className="text-sm text-slate-400">MRR Target</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$25M</div>
                      <div className="text-sm text-slate-400">Payment TPV</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="font-medium mb-2">Key Milestones:</h4>
                    <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-300">
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Gilbarco partnership secured</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Stripe Connect integration live</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> First 150 gas station deployments</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> 150 smoke shop/retail customers</li>
                    </ul>
                  </div>
                </div>

                {/* Year 2 */}
                <div className="bg-gradient-to-r from-cyan-500/5 to-transparent border border-cyan-500/20 rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-cyan-400">Y2</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Year 2: Growth (2027-2028)</h3>
                      <p className="text-slate-400">Market expansion & payment integration</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-bold text-cyan-400">$5M</div>
                      <div className="text-sm text-slate-400">Target ARR</div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">1,000</div>
                      <div className="text-sm text-slate-400">Paying Customers</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$415K/mo</div>
                      <div className="text-sm text-slate-400">MRR Target</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$100M</div>
                      <div className="text-sm text-slate-400">Payment TPV</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="font-medium mb-2">Key Milestones:</h4>
                    <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-300">
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400" /> Verifone partnership live</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400" /> Finix integration (white-label payments)</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400" /> 500+ gas station deployments</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400" /> Expand to 10 key states</li>
                    </ul>
                  </div>
                </div>

                {/* Year 3 */}
                <div className="bg-gradient-to-r from-amber-500/5 to-transparent border border-amber-500/20 rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-amber-400">Y3</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Year 3: Market Leadership (2028-2029)</h3>
                      <p className="text-slate-400">Establish as go-to vertical POS solution</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-2xl font-bold text-amber-400">$15M</div>
                      <div className="text-sm text-slate-400">Target ARR</div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">3,000</div>
                      <div className="text-sm text-slate-400">Paying Customers</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$1.25M/mo</div>
                      <div className="text-sm text-slate-400">MRR Target</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="text-lg font-bold">$300M</div>
                      <div className="text-sm text-slate-400">Payment TPV</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="font-medium mb-2">Key Milestones:</h4>
                    <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-300">
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> 1,500+ gas station locations</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> White-label enterprise deals</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> Series A funding ($15-20M)</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400" /> National coverage (all 50 states active)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6 mt-8">
                <h3 className="font-semibold text-lg mb-4">Revenue Model Breakdown (Year 3)</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <div className="text-sm text-slate-400 mb-2">SaaS Subscriptions</div>
                    <div className="text-3xl font-bold text-emerald-400">$9M</div>
                    <div className="text-sm text-slate-500">60% of revenue</div>
                    <div className="mt-2 text-sm text-slate-400">
                      Avg $250/mo × 3,000 customers
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-2">Payment Processing</div>
                    <div className="text-3xl font-bold text-cyan-400">$3M</div>
                    <div className="text-sm text-slate-500">20% of revenue</div>
                    <div className="mt-2 text-sm text-slate-400">
                      1% take on $300M TPV
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-2">White-Label & Enterprise</div>
                    <div className="text-3xl font-bold text-amber-400">$3M</div>
                    <div className="text-sm text-slate-500">20% of revenue</div>
                    <div className="mt-2 text-sm text-slate-400">
                      10-15 enterprise deals
                    </div>
                  </div>
                </div>
              </div>

              {/* Investment Ask */}
              <div className="bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-amber-500/10 border border-emerald-500/20 rounded-xl p-8 mt-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Investment Opportunity</h3>
                  <p className="text-slate-400">Seeking strategic partners to accelerate growth</p>
                </div>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Series A Round</div>
                    <div className="text-3xl font-bold">$3-5M</div>
                    <div className="text-sm text-slate-500 mt-1">For Year 1-2 execution</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Use of Funds</div>
                    <div className="text-lg font-semibold">Engineering, Sales, Partnerships</div>
                    <div className="text-sm text-slate-500 mt-1">50% / 30% / 20%</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Target Multiple</div>
                    <div className="text-3xl font-bold text-emerald-400">15-30x</div>
                    <div className="text-sm text-slate-500 mt-1">SaaS industry standard</div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>FlowFeed Investor Presentation | Confidential</p>
          <p className="mt-1">For questions: aayazali@gmail.com</p>
        </div>
      </footer>
    </div>
  );
};

export default FeaturedPage;
